import React, { useEffect, useRef, useState } from "react";
import { FeatherMoreHorizontal } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";

/** One line of a row menu. A disabled item stays listed rather than vanishing. */
export interface RateMenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
}

/**
 * The actions Zoho itself offers on a rate, behind the same "⋯" it uses.
 *
 * A trigger rather than a visible link, following master-admin's review step: the
 * table now carries six packed columns including the payable ledger, and a button
 * on every row would take that width off the two columns carrying words. It costs
 * nothing until pressed.
 *
 * Closes on Escape and on a press anywhere outside it, both listened for on the
 * document — the menu is one row of many, and a press on a sibling row means
 * "close this", not "do nothing".
 *
 * ONE ITEM TODAY. The status flip Zoho also offers is a later slice; the menu
 * exists now because the shape of the row should not change again when it lands.
 */
export const RateRowMenu: React.FC<{
  items: RateMenuItem[];
  busy?: boolean;
}> = ({ items, busy = false }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative flex justify-end">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        disabled={busy}
        aria-label="Rate actions"
        aria-expanded={open}
        className={`flex h-6 w-6 items-center justify-center rounded-sm text-subtext-color outline-none hover:bg-neutral-100 hover:text-default-font disabled:opacity-50 ${
          open ? "bg-neutral-100 text-default-font" : ""
        }`}
      >
        <FeatherMoreHorizontal className="text-body font-body" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-44 flex-col items-start rounded-md border border-solid border-neutral-border bg-default-background p-1 shadow-md">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className="block w-full rounded-sm px-2 py-1.5 text-left text-body font-body text-default-font hover:bg-neutral-100 disabled:pointer-events-none disabled:opacity-50"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The panel's own add button, and why it might not work yet.
 *
 * At the BOTTOM of the panel under a divider, one per panel rather than one for
 * the page: the two taxes take different section vocabularies, so "add a rate" is
 * a different form depending on which list you are looking at, and a shared
 * button would have to ask which one first. master-admin's placement.
 *
 * Disabled until the section list is in, because the form's first field is a
 * section and Zoho matches on nothing else. The reason sits beside it rather than
 * in a tooltip: a button that does nothing without saying why is worse than no
 * button.
 */
export const AddRateButton: React.FC<{
  label: string;
  /** Null while the section list is still being read. */
  loaded: boolean;
  error: string | null;
  disabled?: boolean;
  onOpen: () => void;
}> = ({ label, loaded, error, disabled = false, onOpen }) => (
  <div className="flex w-full flex-wrap items-center gap-3 border-t border-solid border-neutral-border pt-3">
    <Button
      variant="neutral-secondary"
      size="small"
      onClick={onOpen}
      disabled={!loaded || disabled}
    >
      Add more {label} rates
    </Button>
    {!loaded && error === null && (
      <span className="text-caption font-caption text-subtext-color">
        Reading the section list…
      </span>
    )}
    {error !== null && (
      <span className="text-caption font-caption text-subtext-color">
        {error}
      </span>
    )}
  </div>
);
